import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { database, subscribeToPath } from '../../services/database';
import { TaskCategory, TaskTemplate, TaskInstance, TaskSubTaskInstance, StaffMember } from '../../types';
import { toast } from 'react-hot-toast';
import TaskExecutionView from './TaskExecutionView';
import ConfirmationModal from '../ui/ConfirmationModal';

interface DailyTasksViewProps {
    onBack: () => void;
    staffName: string;
    staffList?: StaffMember[];
    isAdmin?: boolean;
    initialCategoryId?: string | null;
    onOpenNightAudit?: () => void;
}

const DailyTasksView: React.FC<DailyTasksViewProps> = ({ onBack, staffName, staffList = [], isAdmin = false, initialCategoryId, onOpenNightAudit }) => {
    const [categories, setCategories] = useState<{ [id: string]: TaskCategory }>({});
    const [templates, setTemplates] = useState<{ [id: string]: TaskTemplate }>({});
    const [instances, setInstances] = useState<{ [id: string]: TaskInstance }>({});
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(initialCategoryId || null);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Flow State
    const [templateToStart, setTemplateToStart] = useState<TaskTemplate | null>(null);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [executorName, setExecutorName] = useState(staffName);
    const [existingInstances, setExistingInstances] = useState<TaskInstance[]>([]);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Subscriptions
    useEffect(() => {
        setIsLoading(true);
        const unsubCat = subscribeToPath('taskCategories', (data) => setCategories(data || {}));
        const unsubTpl = subscribeToPath('taskTemplates', (data) => setTemplates(data || {}));
        const unsubInst = subscribeToPath('taskInstances', (data) => {
            setInstances(data || {});
            setIsLoading(false);
        });
        
        return () => {
            unsubCat();
            unsubTpl();
            unsubInst();
        };
    }, []);

    const activeTemplates = useMemo(() => {
        return Object.values(templates).filter(t => t.active && t.categoryId === selectedCategoryId);
    }, [templates, selectedCategoryId]);

    const activeTask = useMemo(() => {
        if (!selectedTaskId) return null;
        return instances[selectedTaskId] || null;
    }, [instances, selectedTaskId]);

    const findExistingInstances = (templateId: string, date: string, name: string) => {
        return Object.values(instances).filter(i => i.templateId === templateId && i.date === date && i.completedBy === name);
    };

    const handleStartClick = (template: TaskTemplate) => {
        setTemplateToStart(template);
        const existing = findExistingInstances(template.id, startDate, executorName);
        setExistingInstances(existing);
        if (existing.length > 0) {
            setShowOptionsModal(true);
        }
    };

    const handleCreateNewInstance = async (template: TaskTemplate, date: string, name: string) => {
        const newInstance: Partial<TaskInstance> = {
            templateId: template.id,
            categoryId: template.categoryId,
            title: template.title,
            description: template.description || '',
            date: date,
            status: 'pending',
            completedBy: name,
            subTasks: [...(template.subTasks || [])]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((st, sIdx) => ({
                    label: st.label,
                    isDone: false,
                    order: st.order || sIdx + 1,
                    required: st.required ?? false,
                    subSteps: [...(st.subSteps || [])]
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map(ss => ({
                            name: ss.name,
                            done: false,
                            order: ss.order,
                            required: ss.required ?? false,
                            useDoubleFields: ss.useDoubleFields ?? false,
                            count: '',
                            details: ''
                        }))
                }))
        };
        
        const created = await database.createTaskInstance(newInstance);
        if (created) {
            setSelectedTaskId(created.id);
            setTemplateToStart(null);
            setShowOptionsModal(false);
        }
    };

    const handleResumeInstance = (instance: TaskInstance) => {
        setSelectedTaskId(instance.id);
        setTemplateToStart(null);
        setShowOptionsModal(false);
    };

    const handleOverrideInstance = async (instanceId: string, template: TaskTemplate, date: string, name: string) => {
        await database.deleteTaskInstance(instanceId);
        await handleCreateNewInstance(template, date, name);
    };

    const handleDeleteInstance = async (id: string) => {
        setIsDeleting(true);
        try {
            await database.deleteTaskInstance(id);
            const remaining = existingInstances.filter(i => i.id !== id);
            setExistingInstances(remaining);
            if (remaining.length === 0) {
                setShowOptionsModal(false);
            }
            toast.success('Instance deleted');
        } catch (error) {
            toast.error('Failed to delete instance');
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12 h-[60vh]">
                <Icons.Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
        );
    }

    if (activeTask) {
        return (
            <TaskExecutionView 
                instance={activeTask}
                staffName={staffName}
                isAdmin={isAdmin}
                onBack={() => setSelectedTaskId(null)}
                onFinalized={() => {}}
            />
        );
    }

    const currentCategory = selectedCategoryId ? categories[selectedCategoryId] : null;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                <div>
                    <button 
                        onClick={onBack}
                        className="flex items-center gap-2 text-xs font-normal text-gray-400 hover:text-indigo-600 uppercase tracking-widest transition-colors mb-3"
                    >
                        <Icons.ArrowLeft className="w-3.5 h-3.5" /> Back to Task Hub
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-1.5 h-10 rounded-full" style={{ backgroundColor: currentCategory?.color || '#6366f1' }} />
                        <h1 className="text-4xl font-normal text-gray-900 dark:text-white uppercase tracking-tight">
                            {currentCategory?.name || 'Department'} Tasks
                        </h1>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeTemplates.length === 0 ? (
                    <div className="md:col-span-2 lg:col-span-3 py-20 bg-gray-50 dark:bg-slate-900 rounded-[40px] border-2 border-dashed border-gray-200 dark:border-slate-800 flex flex-col items-center justify-center text-center">
                        <Icons.ClipboardList className="w-16 h-16 text-gray-300 mb-4" />
                        <h2 className="text-xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-2">No Active Tasks</h2>
                        <p className="text-sm font-normal text-gray-400 max-w-xs">
                            There are no active tasks configured for this department.
                        </p>
                    </div>
                ) : (
                    activeTemplates.map(template => (
                        <div 
                            key={template.id}
                            className="bg-white dark:bg-slate-800 rounded-3xl p-8 border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                        >
                            <div className="flex-grow">
                                <h3 className="text-xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-3">
                                    {template.title}
                                </h3>
                                <p className="text-xs text-gray-500 font-normal leading-relaxed mb-6 line-clamp-3">
                                    {template.description || 'No description available for this template.'}
                                </p>
                            </div>
                            
                            <div className="mt-auto space-y-4 pt-6 border-t border-gray-50 dark:border-slate-700/50">
                                <div className="flex items-center justify-between text-[10px] font-normal uppercase tracking-widest text-gray-400">
                                    <span>{template.subTasks?.length || 0} Steps</span>
                                    <span>{template.subTasks?.filter(s => s.required).length || 0} Required</span>
                                </div>
                                <button 
                                    onClick={() => handleStartClick(template)}
                                    className="w-full py-4 bg-indigo-600 text-white font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 dark:shadow-none uppercase tracking-widest text-[11px] flex items-center justify-center gap-2"
                                >
                                    <Icons.Play className="w-3.5 h-3.5" />
                                    Start New Task
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Start Task Dialog / Options Modal */}
            {templateToStart && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setTemplateToStart(null)} />
                    <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-8 border-b border-gray-100 dark:border-slate-700">
                            <h2 className="text-2xl font-normal text-gray-900 dark:text-white uppercase tracking-tight mb-2">Initialize Task</h2>
                            <p className="text-sm font-normal text-gray-400 uppercase tracking-widest">Setup task details before starting</p>
                        </div>
                        
                        <div className="p-8 space-y-6">
                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 px-1">Effective Date</label>
                                <input 
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setExistingInstances(findExistingInstances(templateToStart.id, e.target.value, executorName));
                                    }}
                                    className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 text-sm font-normal focus:ring-2 focus:ring-indigo-500 transition-all"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-[10px] font-normal text-gray-400 uppercase tracking-widest mb-2 px-1">Assigned Staff</label>
                                <div className="relative">
                                    <Icons.User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <select 
                                        value={executorName}
                                        onChange={(e) => {
                                            const newName = e.target.value;
                                            setExecutorName(newName);
                                            setExistingInstances(findExistingInstances(templateToStart.id, startDate, newName));
                                        }}
                                        className="w-full bg-gray-50 dark:bg-slate-900 border-none rounded-2xl p-4 pl-12 text-sm font-normal focus:ring-2 focus:ring-indigo-500 transition-all appearance-none"
                                    >
                                        <option value="">Select Staff Member</option>
                                        {staffList.filter(s => s.status !== 'deleted' && s.status !== 'suspended').sort((a,b) => a.name.localeCompare(b.name)).map(staff => (
                                            <option key={staff.id} value={staff.name}>{staff.name}</option>
                                        ))}
                                        {!staffList.some(s => s.name === staffName) && (
                                            <option value={staffName}>{staffName} (Me)</option>
                                        )}
                                    </select>
                                    <Icons.ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            {existingInstances.length > 0 && (
                                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 p-5 rounded-2xl">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Icons.AlertTriangle className="w-4 h-4 text-amber-600" />
                                        <span className="text-xs font-normal text-amber-800 dark:text-amber-400 uppercase tracking-widest">Existing Task Found</span>
                                    </div>
                                    <p className="text-xs font-normal text-amber-700/70 dark:text-amber-500/70 leading-relaxed">
                                        There are already {existingInstances.length} task instance(s) for this template on {startDate}. How would you like to proceed?
                                    </p>
                                    
                                    <div className="mt-6 flex flex-col gap-2">
                                        {existingInstances.map(instance => (
                                            <div key={instance.id} className="flex items-center gap-2 bg-white dark:bg-slate-800 p-2 rounded-xl border border-amber-100 dark:border-amber-900/20">
                                                <div className="flex-grow min-w-0 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[10px] font-normal text-gray-900 dark:text-white truncate uppercase tracking-tight">{instance.title}</p>
                                                        {instance.status === 'completed' && (
                                                            <span className="text-[7px] bg-green-100 text-green-700 px-1 rounded font-normal tracking-tighter">DONE</span>
                                                        )}
                                                    </div>
                                                    <p className="text-[8px] font-normal text-gray-400 uppercase tracking-widest">By {instance.completedBy || 'Unknown'}</p>
                                                </div>
                                                <button 
                                                    onClick={() => handleResumeInstance(instance)}
                                                    className={`px-3 py-1.5 text-[10px] font-normal rounded-lg uppercase tracking-tight transition-all ${
                                                        instance.status === 'completed'
                                                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                    }`}
                                                >
                                                    {instance.status === 'completed' ? 'View Report' : 'Resume'}
                                                </button>
                                                <button 
                                                    onClick={() => handleOverrideInstance(instance.id, templateToStart, startDate, executorName)}
                                                    className="px-3 py-1.5 bg-amber-600 text-white text-[10px] font-normal rounded-lg uppercase tracking-tight hover:bg-amber-700 transition-all"
                                                >
                                                    Override
                                                </button>
                                                <button 
                                                    disabled={isDeleting}
                                                    onClick={() => handleDeleteInstance(instance.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                                >
                                                    <Icons.Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-8 bg-gray-50/50 dark:bg-slate-900/20 flex gap-4">
                            <button 
                                onClick={() => setTemplateToStart(null)}
                                className="flex-1 py-4 bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 font-normal rounded-2xl border border-gray-100 dark:border-slate-700 hover:bg-gray-50 transition-all uppercase tracking-widest text-[11px]"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => handleCreateNewInstance(templateToStart, startDate, executorName)}
                                className="flex-[2] py-4 bg-indigo-600 text-white font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none uppercase tracking-widest text-[11px] flex items-center justify-center gap-2"
                            >
                                <Icons.PlusCircle className="w-4 h-4" />
                                Create New Instance
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyTasksView;
